import { component } from 'picoapp';

export default component(async (node, ctx) => {
  const { default: Swiper } = await import(
    /* webpackChunkName: "swiper" */ 'modules/swiper'
  );

  let thumbnailSwiper = null;
  let mainSwiper = null;
  let childComponents = [];

  // Import child components
  const childComponentModules = [
    () => import('./stl-product-fetcher'),
    () => import('./stl-product-modal'),
    () => import('./stl-size-guide-modal'),
    () => import('./stl-lightbox'),
    () => import('./stl-linked-products'),
  ];

  /**
   * Initialize child components
   */
  const initChildComponents = async () => {
    try {
      // Load all child component modules
      const modules = await Promise.all(
        childComponentModules.map((importFn) => importFn()),
      );

      // Initialize each component
      modules.forEach((module) => {
        if (module.default) {
          const childComponent = module.default(node, ctx);
          childComponents.push(childComponent);
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize child components:', error);
    }
  };

  const initThumbnailSwiper = () => {
    const thumbnailElement = node.querySelector('[data-swiper-thumbnails]');
    if (!thumbnailElement) return;

    // Get the section/component ID for unique scrollbar targeting
    const componentId = node.getAttribute('data-component') || 'stl-collection';
    thumbnailSwiper = new Swiper(thumbnailElement, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      scrollbar: {
        el: `[data-slider-scrollbar="${componentId}"]`,
        draggable: true,
        dragClass: 'slider-scrollbar__drag',
        horizontalClass: 'slider-scrollbar--horizontal',
      },
      navigation: {
        nextEl: '.stl-collection__arrow.next',
        prevEl: '.stl-collection__arrow.prev',
      },
      // Hide scrollbar when not needed
      watchSlidesProgress: true,
      on: {
        init() {
          // Hide scrollbar if all slides are visible
          if (this.slides.length <= 5) {
            const scrollbar = this.scrollbar?.el;
            if (scrollbar) {
              scrollbar.style.display = 'none';
            }
          }
        },
      },
    });
  };

  const updateNavigationButtons = () => {
    if (!mainSwiper) return;

    const prevButton = node.querySelector('[data-carousel-prev]');
    const nextButton = node.querySelector('[data-carousel-next]');

    if (prevButton && nextButton) {
      // Update button states based on current slide
      const updateButtonStates = () => {
        const { isBeginning, isEnd } = mainSwiper;

        prevButton.style.opacity = isBeginning ? '0.3' : '1';
        nextButton.style.opacity = isEnd ? '0.3' : '1';

        prevButton.disabled = isBeginning;
        nextButton.disabled = isEnd;
      };

      // Listen for slide changes
      mainSwiper.on('slideChange', updateButtonStates);
      mainSwiper.on('reachBeginning', updateButtonStates);
      mainSwiper.on('reachEnd', updateButtonStates);

      // Initial state
      updateButtonStates();
    }
  };

  const updateSlideCount = () => {
    if (!mainSwiper) return;

    const imageIndex = node.querySelector('[data-image-slide-index]');
    const zoomButton = node.querySelector('[data-lightbox-open]');

    if (imageIndex) {
      mainSwiper.on('slideChange', () => {
        const { activeIndex } = mainSwiper;
        const slideIndex = activeIndex + 1;
        imageIndex.innerText = slideIndex;

        // Update zoom button index
        if (zoomButton) {
          zoomButton.setAttribute('data-index', activeIndex.toString());
        }
      });

      // Set initial values
      imageIndex.innerText = '1';
      if (zoomButton) {
        zoomButton.setAttribute('data-index', '0');
      }
    }
  };

  const initMainSwiper = () => {
    const mainElement = node.querySelector('[data-swiper-main]');
    if (!mainElement) return;

    mainSwiper = new Swiper(mainElement, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      navigation: {
        nextEl: '[data-carousel-next]',
        prevEl: '[data-carousel-prev]',
        disabledClass: 'swiper-button-disabled',
      },
      on: {
        init() {
          // Hide navigation if only one slide
          if (this.slides.length <= 1) {
            const nextBtn = node.querySelector('[data-carousel-next]');
            const prevBtn = node.querySelector('[data-carousel-prev]');
            const slideCount = node.querySelector('[data-slide-count-element]');

            if (nextBtn) nextBtn.style.display = 'none';
            if (prevBtn) prevBtn.style.display = 'none';
            if (slideCount) slideCount.style.display = 'none';
          }
        },
      },
    });

    // Initialize navigation and slide count
    updateNavigationButtons();
    updateSlideCount();
  };

  // Add this at module level to persist across re-initializations
  let isChangingProduct = false;
  let thumbnailHandlersInitialized = false;

  const initThumbnailClickHandlers = () => {
    // Prevent multiple initializations
    if (thumbnailHandlersInitialized) {
      return;
    }

    const thumbnails = node.querySelectorAll('[data-product-thumbnail]');

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', async (e) => {
        e.preventDefault();

        // Prevent multiple rapid clicks
        if (isChangingProduct) {
          return;
        }

        const { productHandle, productId } = thumbnail.dataset;
        isChangingProduct = true;

        // Update active state for all thumbnails
        thumbnails.forEach((t) => {
          t.classList.remove('border-black');
          t.classList.add('border-warm-gray-200');
        });
        // Add active state to clicked thumbnail
        thumbnail.classList.remove('border-warm-gray-200');
        thumbnail.classList.add('border-black');

        // Emit product change event for other components to handle
        const event = new CustomEvent('stl:product:change', {
          detail: {
            handle: productHandle,
            id: productId,
            thumbnail,
          },
        });

        document.dispatchEvent(event);

        // Reset the flag after a short delay
        setTimeout(() => {
          isChangingProduct = false;
        }, 1000);
      });
    });

    thumbnailHandlersInitialized = true;
  };

  /**
   * Handle app remount events
   */
  const handleRemount = () => {
    // Reinitialize thumbnail click handlers for any new thumbnails
    initThumbnailClickHandlers();
  };

  /**
   * Handle swiper update events - when images change
   */
  const handleSwiperUpdate = () => {
    if (mainSwiper) {
      // Update Swiper to recognize new slides
      mainSwiper.update();

      // Reset to first slide
      mainSwiper.slideTo(0, 0); // slide to index 0, with 0ms animation

      // Update navigation visibility based on new slide count
      setTimeout(() => {
        const totalSlides = mainSwiper.slides.length;

        const nextBtn = node.querySelector('[data-carousel-next]');
        const prevBtn = node.querySelector('[data-carousel-prev]');
        const slideCount = node.querySelector('[data-slide-count-element]');

        if (totalSlides <= 1) {
          if (nextBtn) nextBtn.style.display = 'none';
          if (prevBtn) prevBtn.style.display = 'none';
          if (slideCount) slideCount.style.display = 'none';
        } else {
          if (nextBtn) nextBtn.style.display = '';
          if (prevBtn) prevBtn.style.display = '';
          if (slideCount) slideCount.style.display = '';
        }

        // Update the total slide count in pagination
        const slideCountElement = node.querySelector('[data-slide-count-element]');
        if (slideCountElement) {
          // Update the entire content to show "1/X" where X is the new total
          slideCountElement.innerHTML = `<span data-image-slide-index>1</span>/${totalSlides}`;

          // Re-attach the slide change event handler since we replaced the DOM
          const newImageIndex = slideCountElement.querySelector('[data-image-slide-index]');
          if (newImageIndex) {
            // Remove existing listeners to avoid duplicates
            mainSwiper.off('slideChange');
            // Re-attach the slide change handler
            mainSwiper.on('slideChange', () => {
              const { activeIndex } = mainSwiper;
              const slideIndex = activeIndex + 1;
              newImageIndex.innerText = slideIndex;
            });
          }
        }

        // Update navigation buttons state
        updateNavigationButtons();

        // Update zoom button index
        const zoomButton = node.querySelector('[data-lightbox-open]');
        if (zoomButton) {
          zoomButton.setAttribute('data-index', '0'); // Reset to first image
        }
      }, 10);
    }
  };

  const init = async () => {
    initThumbnailSwiper();
    initMainSwiper();
    initThumbnailClickHandlers();

    // Initialize child components
    await initChildComponents();
  };

  const destroy = () => {
    // Cleanup child components
    childComponents.forEach((cleanup) => {
      if (typeof cleanup === 'function') {
        try {
          cleanup();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error during child component cleanup:', error);
        }
      }
    });
    childComponents = [];

    // Cleanup swipers
    if (thumbnailSwiper) {
      thumbnailSwiper.destroy(true, true);
      thumbnailSwiper = null;
    }
    if (mainSwiper) {
      mainSwiper.destroy(true, true);
      mainSwiper = null;
    }

    // Remove remount listener
    document.removeEventListener('app:remount', handleRemount);
    document.removeEventListener('stl:swiper:update', handleSwiperUpdate);
  };

  // Listen for remount events
  document.addEventListener('app:remount', handleRemount);
  document.addEventListener('stl:swiper:update', handleSwiperUpdate);

  init();

  // Return cleanup function
  return destroy;
});
